import { formatDateTime } from '@medplum/core';
import type { DiagnosticReport } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';

const FIELD_CLASSNAME = 'flex flex-col gap-1';
const LABEL_CLASSNAME = 'text-xs font-medium text-muted-foreground uppercase';

type Props = {
  diagnosticReport: DiagnosticReport;
};

/**
 * Renders the subject / performer / issued / status strip that appears above the results
 * table on a `<DiagnosticReportDisplay>`. Pure presentational.
 */
export const DiagnosticReportHeader = ({ diagnosticReport }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const subjectDisplay = diagnosticReport.subject?.display;
  const performerDisplays = (diagnosticReport.performer ?? [])
    .map((p) => p.display)
    .filter((d): d is string => Boolean(d));

  return (
    <div className="flex flex-wrap gap-x-8 gap-y-3">
      {subjectDisplay && (
        <div className={FIELD_CLASSNAME}>
          <div className={LABEL_CLASSNAME}>{t('diagnosticReportSubject')}</div>
          {/* The subject is the patient's name — data-phi lets a "Hide PHI" mode blur it. */}
          <div className="text-sm" data-phi>
            {subjectDisplay}
          </div>
        </div>
      )}
      {performerDisplays.length > 0 && (
        <div className={FIELD_CLASSNAME}>
          <div className={LABEL_CLASSNAME}>{t('diagnosticReportPerformer')}</div>
          <div className="text-sm">{performerDisplays.join(', ')}</div>
        </div>
      )}
      {diagnosticReport.issued && (
        <div className={FIELD_CLASSNAME}>
          <div className={LABEL_CLASSNAME}>{t('diagnosticReportIssued')}</div>
          <div className="text-sm">{formatDateTime(diagnosticReport.issued)}</div>
        </div>
      )}
      {diagnosticReport.status && (
        <div className={FIELD_CLASSNAME}>
          <div className={LABEL_CLASSNAME}>{t('diagnosticReportStatus')}</div>
          <div>
            <Badge variant="outline">{diagnosticReport.status}</Badge>
          </div>
        </div>
      )}
    </div>
  );
};
